const { instance } = require('./url')

const SIGNS_PER_REQUEST = 16

// Must match updateSign()'s validVariables in src/database/sign.js exactly -
// the diff below only ever considers these keys, so a client-only/derived
// property (e.g. a recomputed price field) can never spuriously register as
// "changed" and get sent as an update the server doesn't recognize (which
// previously produced an empty SQL SET clause and crashed - see CRASH.md-
// style incident from a sign save immediately followed by a second save).
const UPDATABLE_SIGN_FIELDS = [
    'given_id', 'type', 'height', 'width', 'quantity',
    'custom_addons', 'addons', 'order_index', 'subsigns',
]

function chunk(array, size) {
    const chunks = []
    for (let i = 0; i < array.length; i += size) {
        chunks.push(array.slice(i, i + size))
    }
    return chunks
}

async function insertSignsRequest(quoteId, signs) {
    return await instance.post('/sign/' + quoteId, {
        'signs': signs
    })
}

async function updateSignsRequest(signs) {
    return await instance.put('/sign', {
        'signs': signs
    })
}

async function deleteSign(id) {
    return await instance.delete('/sign/' + id)
}

/**
 * Diffs prevSigns against signs and applies only what changed - deletes
 * one at a time, inserts/updates chunked by SIGNS_PER_REQUEST, falling
 * back to individual requests for whichever signs are in a chunk that
 * fails, so a save persists as much as it can rather than all-or-nothing.
 * Every phase (delete/insert/update) runs independently, matching that
 * same "save as much as we can" behavior across the whole save.
 * @param {[Sign]} prevSigns
 * @param {[Sign]} signs
 * @param {string} quoteId
 * @return {Promise<[object]>} failures, empty when everything succeeded
 */
async function updateSigns(prevSigns, signs, quoteId) {
    const deleteIds = prevSigns
        .filter((prev) => !signs.find((s) => prev.id === s.id))
        .map((x) => x.id)
    const newSigns = signs.filter((s) => !prevSigns.find((prev) => prev.id === s.id))
    const failures = []

    for (let i = 0; i < deleteIds.length; i++) {
        try {
            await deleteSign(deleteIds[i])
        } catch (error) {
            const prevSign = prevSigns.find((s) => s.id === deleteIds[i])
            failures.push({
                id: deleteIds[i],
                given_id: prevSign ? prevSign.given_id : deleteIds[i],
                action: 'delete',
                error,
            })
        }
    }

    // On success, write the server-assigned id back onto the actual sign
    // objects (same references the caller holds in `signs`) - otherwise
    // a still-id-less new sign would look "new" again on the next save
    // and get inserted a second time as a duplicate row.
    const newSignChunks = chunk(newSigns, SIGNS_PER_REQUEST)
    for (let i = 0; i < newSignChunks.length; i++) {
        try {
            const result = await insertSignsRequest(quoteId, newSignChunks[i])
            // Matched by order_index, NOT array position - a multi-row
            // "INSERT ... VALUES (...), (...) RETURNING *" does not
            // formally guarantee the returned rows come back in the same
            // order as the VALUES list (true in practice almost always,
            // but not a real guarantee) - if it ever doesn't, assigning
            // by position silently attaches the wrong server id to the
            // wrong local sign, and every later save then PUTs that
            // sign's edits onto a different sign's row. order_index is
            // already set uniquely on every sign right before this call
            // (see saveSignsThenFinish() in QuoteViewController.js) and
            // comes back as a real column on each inserted row, so it's
            // a reliable correlation key regardless of return order. A
            // sign whose own order_index isn't found among the returned
            // rows is treated as a failed insert rather than silently
            // left id-less (which would just get re-inserted as a
            // duplicate on the next save).
            for (let j = 0; j < newSignChunks[i].length; j++) {
                const inserted = result.data.signs.find((s) =>
                    s.order_index === newSignChunks[i][j].order_index)
                if (inserted) {
                    newSignChunks[i][j].id = inserted.id
                } else {
                    failures.push({
                        given_id: newSignChunks[i][j].given_id,
                        action: 'insert',
                        error: new Error(
                            'No matching order_index came back for this sign.'),
                    })
                }
            }
        } catch (error) {
            for (let j = 0; j < newSignChunks[i].length; j++) {
                try {
                    const result = await insertSignsRequest(quoteId, [newSignChunks[i][j]])
                    newSignChunks[i][j].id = result.data.signs[0].id
                } catch (individualError) {
                    failures.push({
                        given_id: newSignChunks[i][j].given_id,
                        action: 'insert',
                        error: individualError,
                    })
                }
            }
        }
    }

    // Only changed fields (plus id) go over the wire - given_id rides
    // alongside purely for error reporting below, never inside `payload`,
    // since given_id is itself a real updatable field: including it
    // unconditionally in the diff would silently write it back on every
    // save regardless of whether it actually changed.
    const changedSigns = []
    for (let i = 0; i < prevSigns.length; i++) {
        if (!prevSigns[i].id) {
            continue
        }
        const newSign = signs.find((x) => x.id === prevSigns[i].id)
        if (!newSign) {
            continue
        }

        let changedSign = {id: prevSigns[i].id}
        for (const key of UPDATABLE_SIGN_FIELDS) {
            if (['addons', 'custom_addons', 'subsigns', 'type'].indexOf(key) !== -1) {
                if (JSON.stringify(prevSigns[i][key]) !== JSON.stringify(newSign[key])) {
                    changedSign[key] = newSign[key]
                }
            } else if (prevSigns[i][key] !== newSign[key]) {
                changedSign[key] = newSign[key]
            }
        }

        if (Object.keys(changedSign).length > 1) {
            changedSigns.push({payload: changedSign, given_id: newSign.given_id})
        }
    }

    const changedSignChunks = chunk(changedSigns, SIGNS_PER_REQUEST)
    for (let i = 0; i < changedSignChunks.length; i++) {
        try {
            await updateSignsRequest(changedSignChunks[i].map((x) => x.payload))
        } catch (error) {
            for (let j = 0; j < changedSignChunks[i].length; j++) {
                try {
                    await updateSignsRequest([changedSignChunks[i][j].payload])
                } catch (individualError) {
                    failures.push({
                        id: changedSignChunks[i][j].payload.id,
                        given_id: changedSignChunks[i][j].given_id,
                        action: 'update',
                        error: individualError,
                    })
                }
            }
        }
    }

    return failures
}

export {
    updateSigns,
}
